// !/usr/bin/env node
// -*- coding: utf-8 -*-
/**
@File    : request.js
@Date    : 2025/1/18
@Author  : JIAMID
@Contact : jiamid@qq.com
@Desc    : 统一的HTTP请求管理
*/

import axios from 'axios'
import config from '@/config'

/** 从 axios / API 错误中提取可读文案（后端 HTTPException 返回 { code, msg }） */
export function getApiErrorMessage (error, fallback = '网络错误') {
  const data = error?.response?.data
  if (typeof data?.msg === 'string' && data.msg) return data.msg
  if (typeof data?.message === 'string' && data.message) return data.message
  const detail = data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail)) {
    const text = detail.map(d => d.msg || d.message || String(d)).filter(Boolean).join(' ')
    if (text) return text
  }
  const m = error?.message
  if (typeof m === 'string' && m && !/^Request failed with status code \d+$/i.test(m)) {
    return m
  }
  return fallback
}

// 创建axios实例
const request = axios.create({
  baseURL: config.getApiUrl(),
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器 - 统一添加认证信息
request.interceptors.request.use(
  (config) => {
    // 从localStorage获取token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    console.error('请求拦截器错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器 - 统一处理响应
request.interceptors.response.use(
  (response) => {
    // 如果响应成功，直接返回data字段
    if (response.data.code === 0) {
      return response.data
    } else {
      // 如果业务状态码不为0，抛出错误
      const error = new Error(response.data.msg || response.data.message || '请求失败')
      error.code = response.data.code
      return Promise.reject(error)
    }
  },
  (error) => {
    console.error('响应拦截器错误:', error)

    // 处理401未授权
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      // 跳转到首页
      window.location.href = '/'
      return Promise.reject(new Error('登录已过期，请重新登录'))
    }

    // 处理其他HTTP错误
    return Promise.reject(new Error(getApiErrorMessage(error, '网络错误')))
  }
)

// API方法封装
export const api = {
  // 认证相关
  auth: {
    // 获取验证码
    getSmsCode: (data) => request.post('/auth/gen_sms', data),

    // 用户注册
    register: (data) => request.post('/auth/register', data),

    // 用户登录
    login: (data) => {
      // 登录使用FormData格式
      const formData = new FormData()
      formData.append('username', data.username)
      formData.append('password', data.password)

      return request.post('/auth/login', formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      })
    }
  },

  // 用户相关
  user: {
    // 获取用户信息
    getMe: () => request.get('/me')
  },

  // 麦当劳 MCP / 优惠券相关
  mcd: {
    // 查询当前用户已保存的 MCP Token
    getToken: () => request.get('/mcd/token'),

    // 保存 / 更新 MCP Token
    saveToken: (data) => request.post('/mcd/token', data),

    // 与麦当劳 MCP AI 助手聊天
    chat: (data) => request.post('/mcd/chat', data)
  },

  // 塔罗占卜
  tarot: {
    quota: () => request.get('/tarot/quota'),
    reading: (data) => request.post('/tarot/reading', data)
  },

  // 角色换装
  outfit: {
    get: () => request.get('/outfit'),
    save: (data) => request.put('/outfit', data)
  },

  // 波比签到
  checkin: {
    interact: () => request.post('/checkin/interact')
  },

  // 解析小精灵：B 站 / 抖音无水印链接解析
  video: {
    parse: (data) => request.post('/video/parse', data, { timeout: 60000 })
  },

  // 小百灵音色
  voice: {
    presets: () => request.get('/voice/presets'),
    get: () => request.get('/voice'),
    save: (data) => request.put('/voice', data),
    preview: (data) => request.post('/voice/preview', data, { timeout: 120000 }),
    speak: (data) => request.post('/voice/speak', data, { timeout: 120000 })
  },

  // 寻宝 / 背包
  treasure: {
    inventory: () => request.get('/treasure/inventory'),
    pickup: (data) => request.post('/treasure/pickup', data),
    open: (data) => request.post('/treasure/open', data)
  },

  // 酒馆扑克
  poker: {
    buyIn: (data) => request.post('/poker/buy-in', data),
    cashOut: (data) => request.post('/poker/cash-out', data),
    table: () => request.get('/poker/table')
  }
}
